import type { Resolution } from "./LinesTable";
import type { Issue, JobTarget, LineToSave, ReadLine, Review, SavedKind } from "./readerApi";

/** What the reader's steps decide, as plain functions over the review the
 * server returned and what the office has chosen -- so the drawer's gating
 * can be tested without a PDF. */

/** One line as the save request carries it, or null while an unmatched line
 * is still undecided. */
export function toSave(line: ReadLine, resolution: Resolution | undefined): LineToSave | null {
  if (line.kind !== "unmatched") {
    return {
      seq: line.seq,
      kind: line.kind,
      description: line.description,
      size: line.size,
      qty: line.qty,
      quoted_unit_cost: line.quoted_unit_cost,
      quoted_unit_price: line.quoted_unit_price,
      spare_part_id: line.part?.spare_part_id ?? null,
    };
  }
  if (resolution === undefined) {
    return null;
  }
  const kind: SavedKind = resolution.kind === "part" ? "part" : "sundry";
  return {
    seq: line.seq,
    kind,
    description: resolution.kind === "part" ? resolution.part.description : line.source_description,
    size: resolution.kind === "part" ? resolution.part.size : "",
    qty: line.qty,
    quoted_unit_cost: line.quoted_unit_cost,
    quoted_unit_price: line.quoted_unit_price,
    spare_part_id: resolution.kind === "part" ? resolution.part.spare_part_id : null,
  };
}

/** The blocking issues still standing. An unmatched line stops blocking once
 * the office has said what it is, and an overridable check once the office
 * has overridden it; anything else blocks until the PDF or the providers list
 * is put right. */
function standing(review: Review, resolutions: Record<number, Resolution>, overridden: boolean): Issue[] {
  return review.issues
    .filter((issue) => issue.severity === "blocking")
    .filter((issue) => !(issue.code === "UNMATCHED" && issue.line_seq !== null && resolutions[issue.line_seq]))
    .filter((issue) => !(overridden && issue.overridable));
}

/** The blocking issues still standing, as sentences. `override` is the reason
 * the office gave for overriding; blank means it has not overridden anything. */
export function blockingLeft(review: Review, resolutions: Record<number, Resolution>, override = ""): string[] {
  return standing(review, resolutions, override.trim() !== "").map((issue) =>
    issue.line_seq === null ? issue.message : `Line ${issue.line_seq}: ${issue.message}`,
  );
}

/** The blocking checks the office may override with a reason. */
export function overridableLeft(review: Review): Issue[] {
  return review.issues.filter((issue) => issue.severity === "blocking" && issue.overridable);
}

/** Whether an override could let the quotation through: there is a check to
 * override, and nothing else blocks that the office cannot settle here (an
 * unmatched line it can; an unknown supplier it cannot). Offering the
 * override otherwise would ask for a reason and still hold. */
export function canOverride(review: Review): boolean {
  return (
    overridableLeft(review).length > 0 &&
    review.issues.every((issue) => issue.severity !== "blocking" || issue.overridable || issue.code === "UNMATCHED")
  );
}

export function warnings(review: Review): number {
  return review.issues.filter((issue) => issue.severity === "warning").length;
}

/** "6 lines — 4 parts, labour and travelling". */
export function lineSummary(lines: ReadLine[]): string {
  const parts = lines.filter((line) => line.kind === "part").length;
  const labour = lines.some((line) => line.kind === "labour");
  const travel = lines.some((line) => line.kind === "travel");
  const unmatched = lines.filter((line) => line.kind === "unmatched").length;
  const bits = [
    parts > 0 ? `${parts} matched the price list` : null,
    labour && travel ? "labour and travelling" : labour ? "labour" : travel ? "travelling" : null,
    unmatched > 0 ? `${unmatched} not in the price list` : null,
  ].filter(Boolean);
  return `Read ${lines.length} line${lines.length === 1 ? "" : "s"}${bits.length > 0 ? `: ${bits.join(", ")}` : ""}.`;
}

export type ReaderStep = 1 | 2 | 3 | 4;

export const READER_STEPS: readonly { step: ReaderStep; label: string }[] = [
  { step: 1, label: "Read the PDF" },
  { step: 2, label: "Check lines" },
  { step: 3, label: "Inspection or site" },
  { step: 4, label: "Save" },
];

/** The job the quote itself points at, pre-selected for the office to
 * confirm or change: an inspection already under the quote's work order,
 * else the one site under that number, else an inspection whose site reads
 * like the one the quote names. Several sites sharing the number pick none. */
export function initialTarget(review: Review): JobTarget | null {
  const byWorkOrder = review.candidates.find((c) => c.by_work_order);
  if (byWorkOrder) {
    return { kind: "inspection", id: byWorkOrder.inspection_id };
  }
  const sites = review.work_order?.sites ?? [];
  if (review.work_order !== null && sites.length === 1) {
    return { kind: "site", id: sites[0].id, name: sites[0].name, workOrder: review.work_order.number };
  }
  const suggested = review.candidates.find((c) => c.suggested);
  return suggested ? { kind: "inspection", id: suggested.inspection_id } : null;
}

/** Why the drawer cannot go on from this step, or null when it can.
 * `confirmed` is false while the job is the one read from the quote and the
 * office has not yet confirmed or changed it. `override` is the reason given
 * for overriding the totals check, blank when it has not been overridden. */
export function holdAt(
  step: ReaderStep,
  review: Review | null,
  resolutions: Record<number, Resolution>,
  target: JobTarget | null,
  confirmed = true,
  override = "",
): string | null {
  if (review === null) {
    return "Drop or choose a PDF to continue";
  }
  if (step >= 2) {
    const left = blockingLeft(review, resolutions, override);
    if (left.length > 0) {
      const lines = review.lines.filter((line) => line.kind === "unmatched" && !resolutions[line.seq]);
      if (lines.length > 0) {
        return `Resolve line${lines.length === 1 ? "" : "s"} ${lines.map((line) => line.seq).join(", ")} to continue`;
      }
      // Everything still standing could be overridden: say how to go on.
      return standing(review, resolutions, true).length === 0
        ? "Override the check above, with a reason, to continue"
        : "Something above blocks this quotation";
    }
  }
  if (step >= 3 && target === null) {
    return "Choose the inspection or the site";
  }
  if (step >= 3 && !confirmed) {
    return "Confirm the work order read from the quote, or choose another";
  }
  return null;
}
